// Billing tab (widgetboss only) — one ledger of what each customer has been
// billed across every product. Rows are grouped by agency and read cleanly;
// click a row to edit it in a side panel. Every invoice is Net 30, so a due
// date and "days late" are derived from the invoice date. A blank status or
// amount means "not known yet" and is surfaced under Needs info, never as $0.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  RefreshCw, Plus, Trash2, X, Search, AlertTriangle, CheckCircle2, Clock,
  Receipt, Gift, HelpCircle, Pencil, ChevronDown, ChevronRight, CalendarClock, Handshake,
} from 'lucide-react';

const COMPLIANCE_API_URL =
  process.env.REACT_APP_COMPLIANCE_API ||
  'https://housing-compliance-service.onrender.com';
const COMPLIANCE_ADMIN_KEY =
  process.env.REACT_APP_COMPLIANCE_ADMIN_KEY || process.env.REACT_APP_ADMIN_API_KEY || '';

async function billingFetch(path, options = {}) {
  const url = new URL(`${COMPLIANCE_API_URL}/api/utility-allowance${path}`, window.location.origin);
  if (COMPLIANCE_ADMIN_KEY) url.searchParams.set('key', COMPLIANCE_ADMIN_KEY);
  const res = await fetch(url.toString(), options);
  if (!res.ok) throw new Error(`Billing API error: ${res.status}`);
  return res.json();
}

// ─── Vocabulary ────────────────────────────────────────────────
const PRODUCTS = [
  ['chatbot', 'Chatbot'],
  ['ua', 'Utility Allowances'],
  ['state_reporting', 'State Utility Reporting'],
  ['phone', 'Phone Agent'],
  ['navigator', 'Housing Navigator'],
  ['champ', 'CHAMP (EOHLC)'],
  ['other', 'Other'],
];
const PRODUCT_SHORT = {
  chatbot: 'Chatbot', ua: 'UA', state_reporting: 'State reporting', phone: 'Phone',
  navigator: 'Navigator', champ: 'CHAMP', other: 'Other', '': 'No product',
};
const STATUSES = [
  ['invoiced', 'Invoiced'],
  ['paid', 'Paid'],
  ['partial', 'Partially paid'],
  ['unpaid', 'Unpaid'],
  ['free', 'Free / included'],
];
const METHODS = [
  ['check', 'Check'],
  ['ach', 'ACH / wire'],
  ['stripe', 'Stripe (card)'],
  ['po', 'Purchase order'],
];
const labelOf = (list, v, fallback = '') => (list.find(([k]) => k === v) || [v, v || fallback])[1];

const TERMS_DAYS = 30; // every HousingAI invoice prints Net 30
const OPEN = ['invoiced', 'partial', 'unpaid'];

// ─── Formatting ────────────────────────────────────────────────
const fmtMoney = (n, opts = {}) =>
  n === null || n === undefined || n === ''
    ? '—'
    : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0, ...opts });

const fmtDate = (iso, withYear = false) => {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00');
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', withYear
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric' });
};

const addDays = (iso, n) => {
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d;
};

const startOfToday = () => { const d = new Date(); d.setHours(12, 0, 0, 0); return d; };

// ─── Derived facts about a row ─────────────────────────────────
const isOpen = (r) => OPEN.includes(r.status);
const dueDate = (r) => (r.invoice_date ? addDays(r.invoice_date, TERMS_DAYS) : null);
const daysLate = (r) => {
  if (!isOpen(r)) return null;
  const due = dueDate(r);
  if (!due) return null;
  return Math.floor((startOfToday() - due) / 86400000);
};
const isOverdue = (r) => (daysLate(r) ?? -1) > 0;
const owesInvoice = (r) =>
  /invoice (not yet|outstanding|owed)|not yet (sent|issued|invoiced)|asked to be invoiced|send us a bill|no invoice (yet|sent)/i.test(r.notes || '');

const attention = (r) => {
  const out = [];
  if (!r.status && !isAgreed(r)) out.push('status unknown');
  if (isAgreed(r)) out.push('invoice to send');
  if (isOpen(r) && (r.amount === null || r.amount === undefined)) out.push('no amount');
  if (owesInvoice(r)) out.push('invoice owed to them');
  return out;
};

const RENEW_WINDOW = 60; // days before a term ends that it shows under Renewals
const toDate = (iso) => (iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00') : null);
const daysToTermEnd = (r) => (r.term_end ? Math.floor((toDate(r.term_end) - startOfToday()) / 86400000) : null);
// A term is "up" when it ends within the window or has already ended. Rows that
// were followed by a later term for the same agency + product are superseded
// (e.g. Concord's six months, renewed for a year) and never nag.
const supersededIds = (rows) => {
  const out = new Set();
  for (const r of rows) {
    if (!r.term_end) continue;
    if (rows.some((o) => o.id !== r.id && o.agency === r.agency && o.product === r.product && o.term_end && o.term_end > r.term_end)) out.add(r.id);
  }
  return out;
}
const renewalDue = (r, superseded) => {
  if (superseded.has(r.id)) return false;
  const d = daysToTermEnd(r);
  return d !== null && d <= RENEW_WINDOW;
};
const fmtTerm = (r) => {
  if (!r.term_start && !r.term_end) return '';
  const a = r.term_start ? fmtDate(r.term_start, true) : '?';
  const b = r.term_end ? fmtDate(r.term_end, true) : '?';
  return `${a} – ${b}`;
};
const addMonths = (iso, n) => {
  const d = toDate(iso); if (!d) return '';
  d.setMonth(d.getMonth() + n); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

// Agreed: a price is on the row but nothing has been invoiced or paid yet.
const isAgreed = (r) => !r.status && r.amount !== null && r.amount !== undefined && r.amount !== '';

const hasAmount = (r) => r.amount !== null && r.amount !== undefined && r.amount !== '';
const sumWhere = (rows, pred) => rows.reduce((t, r) => (pred(r) && hasAmount(r) ? t + Number(r.amount) : t), 0);

// ─── Small visual pieces ───────────────────────────────────────
const PRODUCT_TONE = {
  chatbot: 'bg-blue-50 text-blue-700 ring-blue-200',
  ua: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  state_reporting: 'bg-teal-50 text-teal-700 ring-teal-200',
  phone: 'bg-violet-50 text-violet-700 ring-violet-200',
  navigator: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  champ: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200',
  other: 'bg-gray-100 text-gray-700 ring-gray-200',
  '': 'bg-amber-50 text-amber-800 ring-amber-200',
};
const ProductPill = ({ product }) => (
  <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${PRODUCT_TONE[product] || PRODUCT_TONE.other}`}>
    {PRODUCT_SHORT[product] || product}
  </span>
);

const StatusPill = ({ row }) => {
  const late = daysLate(row);
  let tone = 'bg-amber-50 text-amber-800 ring-amber-200';
  let Icon = HelpCircle;
  let text = 'Unknown';
  if (isAgreed(row)) { tone = 'bg-violet-50 text-violet-700 ring-violet-200'; Icon = Handshake; text = 'Agreed'; }
  else if (row.status === 'paid') { tone = 'bg-green-50 text-green-700 ring-green-200'; Icon = CheckCircle2; text = 'Paid'; }
  else if (row.status === 'free') { tone = 'bg-gray-100 text-gray-600 ring-gray-200'; Icon = Gift; text = 'Free'; }
  else if (row.status === 'partial') { tone = 'bg-amber-50 text-amber-800 ring-amber-200'; Icon = Clock; text = 'Partial'; }
  else if (isOpen(row) && late !== null && late > 0) { tone = 'bg-red-50 text-red-700 ring-red-200'; Icon = AlertTriangle; text = `Overdue ${late}d`; }
  else if (row.status === 'unpaid') { tone = 'bg-red-50 text-red-700 ring-red-200'; Icon = AlertTriangle; text = 'Unpaid'; }
  else if (row.status === 'invoiced') { tone = 'bg-sky-50 text-sky-700 ring-sky-200'; Icon = Receipt; text = late !== null ? `Due in ${-late}d` : 'Invoiced'; }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset whitespace-nowrap ${tone}`}>
      <Icon className="h-3 w-3" /> {text}
    </span>
  );
};

const KpiCard = ({ title, value, sub, tone, active, onClick, Icon }) => (
  <button
    onClick={onClick}
    className={`text-left rounded-xl border p-4 transition-shadow hover:shadow-md ${active ? 'ring-2 ring-offset-1 ' + tone.ring : ''} ${tone.bg}`}
  >
    <div className="flex items-center justify-between">
      <span className={`text-xs font-medium uppercase tracking-wide ${tone.text}`}>{title}</span>
      <Icon className={`h-4 w-4 ${tone.text}`} />
    </div>
    <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
    <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
  </button>
);

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
  </label>
);
const inputCls = 'w-full text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white';

// ─── Edit / add panel ──────────────────────────────────────────
const EMPTY = { agency: '', product: '', description: '', invoice_number: '', invoice_date: '', amount: '', status: '', method: '', notes: '', term_start: '', term_end: '' };

const Editor = ({ row, agencies, onClose, onSave, onDelete, busy }) => {
  const isNew = !row?.id;
  const [form, setForm] = useState(() => ({
    ...EMPTY, ...(row || {}), amount: row?.amount ?? '',
    invoice_date: row?.invoice_date ? String(row.invoice_date).slice(0, 10) : '',
    term_start: row?.term_start ? String(row.term_start).slice(0, 10) : '',
    term_end: row?.term_end ? String(row.term_end).slice(0, 10) : '',
  }));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const due = form.invoice_date ? addDays(form.invoice_date, TERMS_DAYS) : null;
  const changed = {};
  for (const k of Object.keys(EMPTY)) {
    const before = row ? (row[k] ?? '') : '';
    const after = form[k] ?? '';
    const isDate = k === 'invoice_date' || k === 'term_start' || k === 'term_end';
    if (String(before).slice(0, isDate ? 10 : undefined) !== String(after)) changed[k] = k === 'amount' ? (after === '' ? null : after) : after;
  }
  const dirty = Object.keys(changed).length > 0;

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{isNew ? 'Add a billing row' : row.agency}</h3>
            <p className="text-xs text-gray-500">{isNew ? 'One row per product an agency has.' : `${labelOf(PRODUCTS, row.product, 'no product')}${row.invoice_number ? ' · ' + row.invoice_number : ''}`}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-gray-500" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <Field label="Agency">
            <input list="billing-agencies" value={form.agency} onChange={set('agency')} className={inputCls} placeholder="e.g. Andover" />
            <datalist id="billing-agencies">{agencies.map((a) => <option key={a} value={a} />)}</datalist>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Product">
              <select value={form.product} onChange={set('product')} className={inputCls}>
                <option value="">— not set —</option>
                {PRODUCTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Status" hint="Leave unset while only the price is agreed; set Invoiced when the invoice goes out. Never enter $0.">
              <select value={form.status} onChange={set('status')} className={inputCls}>
                <option value="">— unknown —</option>
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>
          <Field label="What was sold">
            <input value={form.description} onChange={set('description')} className={inputCls} placeholder="Chat widget, 12 months" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Invoice #">
              <input value={form.invoice_number} onChange={set('invoice_number')} className={inputCls} placeholder="HAI-2026-0000" />
            </Field>
            <Field label="Invoice date" hint={due ? `Net ${TERMS_DAYS} → due ${fmtDate(due.toISOString(), true)}` : `Net ${TERMS_DAYS}`}>
              <input type="date" value={form.invoice_date} onChange={set('invoice_date')} className={inputCls} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (USD)">
              <input type="number" step="0.01" min="0" value={form.amount} onChange={set('amount')} className={inputCls} placeholder="blank = unknown" />
            </Field>
            <Field label="Payment method">
              <select value={form.method} onChange={set('method')} className={inputCls}>
                <option value="">— unknown —</option>
                {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700 inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Contract term</span>
              <span className="text-[11px] text-gray-500">so we know when to follow up</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Term starts">
                <input type="date" value={form.term_start} onChange={set('term_start')} className={inputCls} />
              </Field>
              <Field label="Term ends">
                <input type="date" value={form.term_end} onChange={set('term_end')} className={inputCls} />
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
              <span className="text-gray-500">Set end from start:</span>
              {[[6, '6 months'], [12, '12 months'], [3, '90 days']].map(([m, l]) => (
                <button key={m} type="button" disabled={!form.term_start} onClick={() => setForm((f) => ({ ...f, term_end: addMonths(f.term_start, m) }))}
                  className="px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40">{l}</button>
              ))}
              {form.term_end && (() => { const d = daysToTermEnd({ term_end: form.term_end }); return <span className={`ml-auto ${d < 0 ? 'text-red-600' : d <= RENEW_WINDOW ? 'text-amber-700' : 'text-gray-500'}`}>{d < 0 ? `ended ${-d} days ago` : d === 0 ? 'ends today' : `ends in ${d} days`}</span>; })()}
            </div>
          </div>
          <Field label="Notes" hint="Discounts, PO numbers, who to invoice, what the email trail says.">
            <textarea value={form.notes} onChange={set('notes')} rows={6} className={inputCls + ' resize-y'} />
          </Field>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t bg-gray-50">
          {!isNew ? (
            <button onClick={() => onDelete(row)} className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 px-2 py-1.5 rounded hover:bg-red-50">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-white">Cancel</button>
            <button
              disabled={busy || !dirty || !form.agency.trim()}
              onClick={() => onSave(isNew ? { ...changed, agency: form.agency.trim() } : changed)}
              className="px-3 py-1.5 text-sm rounded-md bg-gray-900 text-white hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? 'Saving…' : isNew ? 'Add row' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── One billing line ──────────────────────────────────────────
const Row = ({ r, onEdit, superseded }) => {
  const flags = attention(r);
  const dte = daysToTermEnd(r);
  const renew = renewalDue(r, superseded);
  const due = dueDate(r);
  const [open, setOpen] = useState(false);
  const longNotes = (r.notes || '').length > 110;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onEdit(r)}
      onKeyDown={(e) => { if (e.key === 'Enter') onEdit(r); }}
      className={`group grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[7rem_minmax(0,1fr)_7rem_8.5rem_2rem] gap-x-3 gap-y-1 items-start px-4 py-2.5 border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${isOverdue(r) ? 'bg-red-50/40' : ''}`}
    >
      <div className="pt-0.5 md:order-none order-1"><ProductPill product={r.product} /></div>
      <div className="min-w-0 col-span-2 md:col-span-1 order-3 md:order-none">
        <div className="text-sm text-gray-900 truncate">{r.description || <span className="text-gray-400 italic">no description</span>}</div>
        <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
          {r.invoice_number && <span className="font-mono">{r.invoice_number}</span>}
          {r.invoice_date && <span>sent {fmtDate(r.invoice_date)}</span>}
          {due && isOpen(r) && <span className={isOverdue(r) ? 'text-red-600 font-medium' : ''}>due {fmtDate(due.toISOString())}</span>}
          {r.method && <span>via {labelOf(METHODS, r.method)}</span>}
          {fmtTerm(r) && (
            <span className={`inline-flex items-center gap-1 ${renew ? (dte < 0 ? 'text-red-600 font-medium' : 'text-amber-700 font-medium') : ''}`}>
              <CalendarClock className="h-3 w-3" /> term {fmtTerm(r)}
              {dte !== null && !superseded.has(r.id) && (dte < 0 ? ` · ended ${-dte}d ago` : dte === 0 ? ' · ends today' : ` · ${dte}d left`)}
              {superseded.has(r.id) && ' · renewed'}
            </span>
          )}
        </div>
        {r.notes && (
          <div className="text-xs text-gray-600 mt-1">
            <span className={open ? '' : 'line-clamp-1'}>{r.notes}</span>
            {longNotes && (
              <button onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} className="text-blue-600 hover:underline ml-1">{open ? 'less' : 'more'}</button>
            )}
          </div>
        )}
        {(flags.length > 0 || renew) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {renew && (
              <span className={`inline-flex items-center gap-1 text-[11px] rounded px-1.5 py-0.5 ring-1 ring-inset ${dte < 0 ? 'text-red-700 bg-red-50 ring-red-200' : 'text-amber-800 bg-amber-50 ring-amber-200'}`}>
                <CalendarClock className="h-3 w-3" /> {r.status === 'free' ? 'free period' : 'term'} {dte < 0 ? 'ended, follow up' : 'ending, follow up'}
              </span>
            )}
            {flags.map((f) => (
              <span key={f} className="inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-50 ring-1 ring-inset ring-amber-200 rounded px-1.5 py-0.5">
                <AlertTriangle className="h-3 w-3" /> {f}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className={`text-sm text-right tabular-nums pt-0.5 order-2 md:order-none ${hasAmount(r) && !(r.status === 'free' && Number(r.amount) === 0) ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
        {r.status === 'free' && (!hasAmount(r) || Number(r.amount) === 0) ? 'free' : fmtMoney(r.amount)}
      </div>
      <div className="pt-0.5 order-2 md:order-none justify-self-end md:justify-self-start"><StatusPill row={r} /></div>
      <button onClick={(e) => { e.stopPropagation(); onEdit(r); }} className="hidden md:block p-1 rounded text-gray-300 group-hover:text-gray-600 hover:bg-gray-200" aria-label={`Edit ${r.agency} ${PRODUCT_SHORT[r.product] || ''}`}>
        <Pencil className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// ─── Agency card ───────────────────────────────────────────────
const AgencyCard = ({ agency, rows, onEdit, collapsed, onToggle, superseded }) => {
  const outstanding = sumWhere(rows, isOpen);
  const renewals = rows.filter((r) => renewalDue(r, superseded)).length;
  const paid = sumWhere(rows, (r) => r.status === 'paid');
  const flags = rows.reduce((n, r) => n + attention(r).length, 0);
  const overdue = rows.some(isOverdue);
  const products = [...new Set(rows.map((r) => r.product))];
  return (
    <section className={`rounded-xl border bg-white shadow-sm overflow-hidden ${overdue ? 'border-red-200' : 'border-gray-200'}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50">
        {collapsed ? <ChevronRight className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        <h3 className="text-sm font-semibold text-gray-900 flex-1 min-w-0 truncate" title={agency}>{agency}</h3>
        <div className="hidden sm:flex items-center gap-1">{products.map((p) => <ProductPill key={p} product={p} />)}</div>
        {renewals > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-50 ring-1 ring-inset ring-amber-200 rounded-full px-2 py-0.5" title="term ending or ended">
            <CalendarClock className="h-3 w-3" /> {renewals}
          </span>
        )}
        {flags > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-50 ring-1 ring-inset ring-amber-200 rounded-full px-2 py-0.5" title="missing info">
            <AlertTriangle className="h-3 w-3" /> {flags}
          </span>
        )}
        <div className="text-xs text-gray-500 text-right sm:w-44 tabular-nums whitespace-nowrap">
          {outstanding > 0 && <span className={overdue ? 'text-red-600 font-medium' : 'text-sky-700'}>{fmtMoney(outstanding)} open</span>}
          {outstanding > 0 && paid > 0 && ' · '}
          {paid > 0 && <span className="text-green-700">{fmtMoney(paid)} paid</span>}
          {outstanding === 0 && paid === 0 && <span>nothing billed</span>}
        </div>
      </button>
      {!collapsed && rows.map((r) => <Row key={r.id} r={r} onEdit={onEdit} superseded={superseded} />)}
    </section>
  );
};

// ─── The tab ───────────────────────────────────────────────────
const VIEWS = [
  ['all', 'Everything'],
  ['overdue', 'Overdue'],
  ['open', 'Outstanding'],
  ['agreed', 'Agreed, unbilled'],
  ['renewals', 'Renewals'],
  ['attention', 'Needs info'],
  ['paid', 'Paid'],
  ['free', 'Free'],
];
const SORTS = [
  ['az', 'Agency A–Z'],
  ['overdue', 'Most overdue first'],
  ['open', 'Largest balance first'],
  ['renew', 'Renews soonest'],
  ['recent', 'Most recent invoice'],
];

const BillingTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // row object, or {} for new
  const [view, setView] = useState('all');
  const [product, setProduct] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('az');
  const [collapsed, setCollapsed] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await billingFetch('/admin/billing');
      setRows(data.rows || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const save = async (changes) => {
    setBusy(true);
    setError(null);
    try {
      if (editing?.id) {
        const data = await billingFetch(`/admin/billing/${editing.id}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changes),
        });
        setRows((rs) => rs.map((r) => (r.id === editing.id ? data.row : r)));
        setToast(`Saved ${data.row.agency}`);
      } else {
        const data = await billingFetch('/admin/billing', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changes),
        });
        setRows((rs) => [...rs, data.row]);
        setToast(`Added ${data.row.agency}`);
      }
      setEditing(null);
    } catch (e) {
      setError(`Save failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the ${r.agency} ${PRODUCT_SHORT[r.product] || ''} row? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await billingFetch(`/admin/billing/${r.id}`, { method: 'DELETE' });
      setRows((rs) => rs.filter((x) => x.id !== r.id));
      setEditing(null);
      setToast(`Deleted ${r.agency}`);
    } catch (e) {
      setError(`Delete failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // ── Totals over everything (cards never change with filters) ──
  const superseded = useMemo(() => supersededIds(rows), [rows]);
  const totals = useMemo(() => ({
    renewals: rows.filter((r) => renewalDue(r, superseded)).length,
    renewalsEnded: rows.filter((r) => renewalDue(r, superseded) && daysToTermEnd(r) < 0).length,
    noTerm: rows.filter((r) => !r.term_end && (isOpen(r) || r.status === 'paid' || r.status === 'free')).length,
    open: sumWhere(rows, isOpen),
    openCount: rows.filter(isOpen).length,
    agreed: sumWhere(rows, isAgreed),
    agreedCount: rows.filter(isAgreed).length,
    overdue: sumWhere(rows, isOverdue),
    overdueCount: rows.filter(isOverdue).length,
    paid: sumWhere(rows, (r) => r.status === 'paid'),
    paidCount: rows.filter((r) => r.status === 'paid').length,
    attentionCount: rows.filter((r) => attention(r).length > 0).length,
    noStatus: rows.filter((r) => !r.status).length,
  }), [rows, superseded]);

  // ── Filter + group + sort ──
  const q = query.trim().toLowerCase();
  const visible = rows.filter((r) => {
    if (product && r.product !== product) return false;
    if (q && ![r.agency, r.description, r.invoice_number, r.notes].some((s) => (s || '').toLowerCase().includes(q))) return false;
    switch (view) {
      case 'overdue': return isOverdue(r);
      case 'open': return isOpen(r);
      case 'renewals': return renewalDue(r, superseded);
      case 'agreed': return isAgreed(r);
      case 'attention': return attention(r).length > 0;
      case 'paid': return r.status === 'paid';
      case 'free': return r.status === 'free';
      default: return true;
    }
  });

  const groups = useMemo(() => {
    const m = new Map();
    for (const r of visible) {
      const k = r.agency || '(no agency)';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    for (const list of m.values()) {
      list.sort((a, b) => String(b.invoice_date || '').localeCompare(String(a.invoice_date || '')) || a.id - b.id);
    }
    const arr = [...m.entries()];
    const maxLate = (list) => Math.max(...list.map((r) => daysLate(r) ?? -9999));
    const latest = (list) => Math.max(...list.map((r) => (r.invoice_date ? new Date(r.invoice_date).getTime() : 0)));
    arr.sort(([a, la], [b, lb]) => {
      if (sort === 'overdue') return maxLate(lb) - maxLate(la) || a.localeCompare(b);
      if (sort === 'open') return sumWhere(lb, isOpen) - sumWhere(la, isOpen) || a.localeCompare(b);
      if (sort === 'recent') return latest(lb) - latest(la) || a.localeCompare(b);
      if (sort === 'renew') {
        const soon = (list) => Math.min(...list.map((r) => (r.term_end && !superseded.has(r.id) ? daysToTermEnd(r) : 99999)));
        return soon(la) - soon(lb) || a.localeCompare(b);
      }
      return a.localeCompare(b);
    });
    return arr;
  }, [visible, sort, superseded]);

  const agencies = useMemo(() => [...new Set(rows.map((r) => r.agency))].sort(), [rows]);
  const allCollapsed = groups.length > 0 && groups.every(([a]) => collapsed.has(a));
  const toggle = (a) => setCollapsed((s) => { const n = new Set(s); n.has(a) ? n.delete(a) : n.add(a); return n; });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Billing</h2>
          <p className="text-sm text-gray-500">
            What every customer has been billed, across all products. Click a row to edit it.
          </p>
          <p className="text-sm text-gray-700 mt-1">
            <span className="font-semibold">{fmtMoney(totals.paid + totals.open + totals.agreed)} contracted</span>
            <span className="text-gray-500"> = {fmtMoney(totals.paid)} collected + {fmtMoney(totals.open)} invoiced + {fmtMoney(totals.agreed)} agreed but not yet billed</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing({})} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-gray-900 text-white hover:bg-black">
            <Plus className="h-3.5 w-3.5" /> Add row
          </button>
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50" aria-label="Refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard
          title="Outstanding" Icon={Receipt}
          value={fmtMoney(totals.open)} sub={`${totals.openCount} open invoices`}
          tone={{ bg: 'bg-white border-gray-200', text: 'text-sky-700', ring: 'ring-sky-300' }}
          active={view === 'open'} onClick={() => setView(view === 'open' ? 'all' : 'open')}
        />
        <KpiCard
          title="Overdue" Icon={AlertTriangle}
          value={fmtMoney(totals.overdue)} sub={totals.overdueCount ? `${totals.overdueCount} past Net ${TERMS_DAYS}` : 'nothing past due'}
          tone={{ bg: totals.overdueCount ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200', text: 'text-red-700', ring: 'ring-red-300' }}
          active={view === 'overdue'} onClick={() => setView(view === 'overdue' ? 'all' : 'overdue')}
        />
        <KpiCard
          title="Agreed, unbilled" Icon={Handshake}
          value={fmtMoney(totals.agreed)} sub={`${totals.agreedCount} rows to invoice`}
          tone={{ bg: 'bg-white border-gray-200', text: 'text-violet-700', ring: 'ring-violet-300' }}
          active={view === 'agreed'} onClick={() => setView(view === 'agreed' ? 'all' : 'agreed')}
        />
        <KpiCard
          title="Collected" Icon={CheckCircle2}
          value={fmtMoney(totals.paid)} sub={`${totals.paidCount} paid`}
          tone={{ bg: 'bg-white border-gray-200', text: 'text-green-700', ring: 'ring-green-300' }}
          active={view === 'paid'} onClick={() => setView(view === 'paid' ? 'all' : 'paid')}
        />
        <KpiCard
          title="Renewals" Icon={CalendarClock}
          value={totals.renewals} sub={totals.renewals ? `${totals.renewalsEnded} ended · ${totals.renewals - totals.renewalsEnded} ending within ${RENEW_WINDOW} days` : `nothing ends within ${RENEW_WINDOW} days`}
          tone={{ bg: totals.renewalsEnded ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200', text: 'text-amber-700', ring: 'ring-amber-300' }}
          active={view === 'renewals'} onClick={() => setView(view === 'renewals' ? 'all' : 'renewals')}
        />
        <KpiCard
          title="Needs info" Icon={HelpCircle}
          value={totals.attentionCount} sub="rows missing status, amount, or owed an invoice"
          tone={{ bg: 'bg-white border-gray-200', text: 'text-amber-700', ring: 'ring-amber-300' }}
          active={view === 'attention'} onClick={() => setView(view === 'attention' ? 'all' : 'attention')}
        />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex flex-wrap rounded-lg bg-gray-100 p-1">
          {VIEWS.map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} className={`px-2.5 py-1 rounded-md text-xs font-medium ${view === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>{l}</button>
          ))}
        </div>
        <select aria-label="Filter by product" value={product} onChange={(e) => setProduct(e.target.value)} className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white">
          <option value="">All products</option>
          {PRODUCTS.map(([v, l]) => <option key={v} value={v}>{l} ({rows.filter((r) => r.product === v).length})</option>)}
        </select>
        <select aria-label="Sort agencies" value={sort} onChange={(e) => setSort(e.target.value)} className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white">
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button
          onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(groups.map(([a]) => a)))}
          className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1.5"
        >
          {allCollapsed ? 'Expand all' : 'Collapse all'}
        </button>
        <div className="relative ml-auto">
          <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search agency, invoice #, notes"
            aria-label="Search billing rows"
            className="text-xs border border-gray-300 rounded-md pl-7 pr-2 py-1.5 w-64 bg-white"
          />
        </div>
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      {/* Groups */}
      <div className="text-xs text-gray-500">
        {groups.length} {groups.length === 1 ? 'agency' : 'agencies'} · {visible.length} rows
        {view !== 'all' || product || q ? <> · <button onClick={() => { setView('all'); setProduct(''); setQuery(''); }} className="text-blue-600 hover:underline">clear filters</button></> : null}
      </div>
      <div className="space-y-3">
        {groups.map(([agency, list]) => (
          <AgencyCard key={agency} agency={agency} rows={list} onEdit={setEditing} collapsed={collapsed.has(agency)} onToggle={() => toggle(agency)} superseded={superseded} />
        ))}
        {!groups.length && !loading && (
          <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-400">Nothing matches.</div>
        )}
        {loading && !rows.length && (
          <div className="rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-400">Loading…</div>
        )}
      </div>

      <p className="text-[11px] text-gray-400">
        Totals only count rows with an amount; a blank amount means unknown, never $0. A row with an amount and no status is an agreed price nothing has been billed against yet. Due dates assume Net {TERMS_DAYS}.
        {totals.noTerm > 0 && <> {totals.noTerm} billed or free rows have no term dates yet.</>}
      </p>

      {editing !== null && (
        <Editor
          row={editing.id ? editing : null}
          agencies={agencies}
          onClose={() => setEditing(null)}
          onSave={save}
          onDelete={remove}
          busy={busy}
        />
      )}
      {toast && (
        <div className="fixed bottom-5 right-5 z-[80] rounded-md bg-gray-900 text-white text-sm px-3 py-2 shadow-lg">{toast}</div>
      )}
    </div>
  );
};

export default BillingTab;
